import { useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

const INACTIVITY_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

export const useInactivityLogout = (isAuthenticated: boolean) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isLoggingOutRef = useRef(false);

  const logout = useCallback(async () => {
    if (isLoggingOutRef.current) return;
    isLoggingOutRef.current = true;

    console.log("useInactivityLogout: Signing out due to inactivity");
    try {
      // Prefer local sign-out to guarantee the browser session is cleared
      // even if the server-side session was already removed.
      await supabase.auth.signOut({ scope: 'local' });
    } catch (error) {
      console.error("useInactivityLogout: Error signing out:", error);
      // Fallback: hard-clear any Supabase auth storage keys.
      try {
        Object.keys(localStorage)
          .filter((k) => k.startsWith('sb-'))
          .forEach((k) => localStorage.removeItem(k));
      } catch {
        // ignore
      }
    } finally {
      isLoggingOutRef.current = false;
    }
  }, []);

  const resetTimer = useCallback(() => {
    if (!isAuthenticated) return;
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      logout();
    }, INACTIVITY_TIMEOUT_MS);
  }, [isAuthenticated, logout]);

  useEffect(() => {
    if (!isAuthenticated) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      return;
    }

    // Activity events to listen for
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    
    // Start the timer
    resetTimer();

    // Reset timer on any activity
    const handleActivity = () => resetTimer();
    
    events.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Also handle visibility change - if tab becomes hidden, start countdown
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab is hidden, keep the timer running
        console.log("useInactivityLogout: Tab hidden, timer continues");
      } else {
        // Tab is visible again, reset timer
        resetTimer();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, resetTimer]);
};

